/**
 * The Object Editor's 2D drawing board (SVG): the object's plan glyph in its
 * own `[u, n]` meter frame, +n up, the default footprint centered on the
 * origin.
 *
 * Edit mode: the EVALUATED glyph (repeats, conditions) is drawn faintly as
 * an underlay, the authored shapes on top as the editable layer. Test mode:
 * only the evaluated glyph, at the test footprint, whose edges drag like a
 * placed object's resize handles.
 *
 * Interaction: wheel zooms at the cursor; middle button, Space+drag or the
 * hand tool pans; the select tool picks (Shift adds), moves (snapped),
 * marquee-selects and moves; the point tool (like a design app's node
 * tool) drags the nodes and a curve's bezier handles of a single selected
 * shape; the
 * selection gets the app's transform gizmo (move / rotate / scale), drawn
 * by a transparent three.js layer over the SVG; the drawing tools snap to the grid and to existing points, Shift constrains to
 * 45° steps.
 */

import classNames from 'classnames';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { DEFAULT_OPENING_WALL_THICKNESS, type ObjectDefinitionSpec, type ObjectPathNode, type ObjectPathShape, type ObjectShape, type Vec2 } from 'shared-types';
import { TransformGizmo } from '../../../potree/personas/tools/modules/transformGizmo/TransformGizmo';
import { defaultCustomParams, evaluateCustomGlyph, resolveAuthoredShapes, tessellateObjectShape } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { customHandleLayout, dimensionOffsetOf } from '../../../potree/sketchObjects/custom/customSketchObjectType';
import type { SketchObjectFrame } from '../../../potree/sketchObjects/types';
import { T } from '../../../translation/src';
import { useSelectionActions } from '../useSelectionActions';
import { CanvasContextMenu, type CanvasContextMenuItem } from './CanvasContextMenu';
import { boundsIntersect, constrainAngle, distanceToShape, movePathHandle, moveShapeNode, pathHandles, shapeBounds, shapeNodes, snapCandidates, snapPoint, togglePathNodeSmooth, type Bounds, type PathHandleSide } from '../document/geometry';
import { NO_SELECTION } from '../document/EditorStore';
import { dragPinnedNode, nodeDraggable, pinStateOf } from '../document/pins';
import { addShapes, footprintEdgeResize, moveFootprintEdge, newShapeId, roundCoord, translateShapes, updateShape, type FootprintEdge } from '../document/specOps';
import { DRAWING_TOOLS, testSize, useEditorState, useObjectEditor, type TestInstance } from '../EditorContext';
import { ShapesGizmoBinding } from './ShapesGizmoBinding';
import { TestInstanceGizmoBinding } from './TestInstanceGizmoBinding';
import { dragTestDepthHandle, dragTestRunEnd, testCenter, testFrame, testHandles, type TestHandle } from './testInstance';
import { dragTestOpeningJamb, dragTestWallBend, openingEditGuides, testOpeningGlyph, testOpeningHandles, testWallBendHandle, testWallPieces } from './testOpening';
import { chainedSegmentsPath, fitViewport, formatLength, gridSteps, isTurnedView, panBy, polylinePath, segmentsPath, toScreen, toWorld, visibleWorld, zoomAt, type CanvasSize, type Viewport } from './viewport';

/** Pick / snap radius in screen pixels. */
const HIT_PX = 7;
/** A press that moves less than this is a click. */
const DRAG_PX = 3;

type Drawing =
  | { kind: 'line' | 'rect' | 'circle'; start: Vec2; pressed: boolean }
  | { kind: 'polyline'; points: Vec2[] }
  | { kind: 'arc'; center: Vec2; start: Vec2 | null }
  /** Pen: `drag` = the node being pulled out into a smooth one (press position in screen px); `closing` = the press was on the first node. */
  | { kind: 'pen'; nodes: ObjectPathNode[]; drag: { index: number; x: number; y: number } | null; closing: boolean };

const mirrorPoint = (anchor: Vec2, q: Vec2): Vec2 => [2 * anchor[0] - q[0], 2 * anchor[1] - q[1]];

/** The last point of an in-progress drawing (Shift constrains the next one to 45° steps from it). */
/** A polyline / pen drawing ended where it is (Enter, right-click): its last placed point ends it, not the cursor. */
function finishedShape(drawing: Drawing): ObjectShape | null {
  const last = lastDrawingPoint(drawing);
  return last ? shapeFromDrawing(drawing, last) : null;
}

function lastDrawingPoint(drawing: Drawing | null): Vec2 | undefined {
  if (!drawing) return undefined;
  switch (drawing.kind) {
    case 'polyline':
      return drawing.points[drawing.points.length - 1];
    case 'pen':
      return drawing.nodes[drawing.nodes.length - 1]?.point;
    case 'arc':
      return drawing.start ?? drawing.center;
    default:
      return drawing.start;
  }
}

type Drag =
  | { kind: 'pan'; x: number; y: number; viewport: Viewport }
  | { kind: 'move'; ids: readonly string[]; base: ObjectDefinitionSpec; grab: Vec2; x: number; y: number; moved: boolean }
  /** `spec`: the draft when the drag began (pinned points re-measure their distance from it). */
  | { kind: 'node'; id: string; index: number; base: ObjectShape; spec: ObjectDefinitionSpec }
  | { kind: 'marquee'; start: Vec2; additive: boolean }
  /** A test object's resize handle, from the instance as the drag began. */
  | { kind: 'test'; handle: TestHandle; before: TestInstance }
  /** A test door / window's jamb (the width follows, centered). */
  | { kind: 'testJamb'; before: TestInstance }
  /** The test wall's bend handle (the radius follows). */
  | { kind: 'testBend'; before: TestInstance }
  | { kind: 'gizmo' }
  | { kind: 'pathHandle'; id: string; index: number; side: PathHandleSide; base: ObjectPathShape }
  /** Where the edge is (`position`, `symmetric`) is only previewed; the resize applies on release. */
  | { kind: 'footprint'; edge: FootprintEdge; position: number | null; symmetric: boolean };

/** The footprint's edge handles (edit mode): which edge and where along it (fraction of the footprint). */
const FOOTPRINT_EDGES: ReadonlyArray<{ edge: FootprintEdge; at: Vec2; cursor: string }> = [
  { edge: 'u+', at: [0.5, 0], cursor: 'cursor-ew-resize' },
  { edge: 'u-', at: [-0.5, 0], cursor: 'cursor-ew-resize' },
  { edge: 'n+', at: [0, 0.5], cursor: 'cursor-ns-resize' },
  { edge: 'n-', at: [0, -0.5], cursor: 'cursor-ns-resize' },
];

/** The gizmo layer: an orthographic camera matching the SVG viewport, rendering only the gizmo. */
interface GizmoOverlay {
  /** Test mode: the gizmo on the stand-in object (move, scale width/depth). */
  testBinding: TestInstanceGizmoBinding;
  renderer: THREE.WebGLRenderer;
  camera: THREE.OrthographicCamera;
  gizmo: TransformGizmo;
  binding: ShapesGizmoBinding;
}

/** Match the overlay camera to the viewport (+n up = +y, looking down −z) and redraw. */
function drawGizmoOverlay({ renderer, camera, gizmo }: GizmoOverlay, view: Viewport, size: CanvasSize): void {
  // Compare the drawing buffer, not the CSS box: potree.css stretches every canvas to 100% already.
  const buffer = renderer.getSize(new THREE.Vector2());
  if (buffer.x !== size.width || buffer.y !== size.height || renderer.getPixelRatio() !== window.devicePixelRatio) {
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(size.width, size.height);
  }
  camera.left = -size.width / 2 / view.scale;
  camera.right = size.width / 2 / view.scale;
  camera.top = size.height / 2 / view.scale;
  camera.bottom = -size.height / 2 / view.scale;
  camera.position.set(view.cu, view.cn, 10);
  // A turned view (wall side on top) rolls the camera half a turn, like the SVG drawing.
  camera.rotation.set(0, 0, view.turned ? Math.PI : 0);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld();
  renderer.clear();
  if (!gizmo.getBinding()) return;
  gizmo.update(camera, renderer.domElement);
  renderer.render(gizmo.scene, camera);
}

const originFrame = (width: number, depth: number): SketchObjectFrame => ({ center: { x: 0, y: 0 }, dir: { x: 1, y: 0 }, norm: { x: 0, y: 1 }, width, depth, z: 0 });

/** What to fit in view: a door / window with its wall stubs and swing, else the footprint. */
const fitExtent = (spec: ObjectDefinitionSpec, width: number, depth: number): { width: number; depth: number } => (spec.opening ? { width: width + 1.6, depth: 2 * width } : { width, depth });

/** Shortest signed sweep from angle a to angle b. */
const shortestSweep = (a: number, b: number): number => {
  let d = b - a;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
};

/** The shape a finished drawing gesture makes, or null when it is degenerate. */
function shapeFromDrawing(drawing: Drawing, end: Vec2): ObjectShape | null {
  const id = newShapeId();
  const r = (p: Vec2): Vec2 => [roundCoord(p[0]), roundCoord(p[1])];
  switch (drawing.kind) {
    case 'line':
      return Math.hypot(end[0] - drawing.start[0], end[1] - drawing.start[1]) > 1e-3 ? { id, kind: 'polyline', points: [r(drawing.start), r(end)], closed: false } : null;
    case 'rect': {
      const min: Vec2 = [Math.min(drawing.start[0], end[0]), Math.min(drawing.start[1], end[1])];
      const max: Vec2 = [Math.max(drawing.start[0], end[0]), Math.max(drawing.start[1], end[1])];
      return max[0] - min[0] > 1e-3 && max[1] - min[1] > 1e-3 ? { id, kind: 'rect', min: r(min), max: r(max) } : null;
    }
    case 'circle': {
      const radius = roundCoord(Math.hypot(end[0] - drawing.start[0], end[1] - drawing.start[1]));
      return radius > 1e-3 ? { id, kind: 'circle', center: r(drawing.start), radius } : null;
    }
    case 'arc': {
      if (!drawing.start) return null;
      const radius = roundCoord(Math.hypot(drawing.start[0] - drawing.center[0], drawing.start[1] - drawing.center[1]));
      const a0 = Math.atan2(drawing.start[1] - drawing.center[1], drawing.start[0] - drawing.center[0]);
      const sweep = shortestSweep(a0, Math.atan2(end[1] - drawing.center[1], end[0] - drawing.center[0]));
      return radius > 1e-3 && Math.abs(sweep) > 1e-3 ? { id, kind: 'arc', center: r(drawing.center), radius, startAngle: a0, endAngle: a0 + sweep } : null;
    }
    case 'pen': {
      // Double-clicks and re-clicks leave duplicate anchors: keep the first (with its handles).
      const nodes = drawing.nodes.filter((n, i, all) => i === 0 || Math.hypot(n.point[0] - all[i - 1].point[0], n.point[1] - all[i - 1].point[1]) > 1e-4);
      if (nodes.length < 2) return null;
      const closed = drawing.closing && nodes.length > 2;
      return { id, kind: 'path', nodes: nodes.map((n) => ({ point: r(n.point), ...(n.in ? { in: r(n.in) } : {}), ...(n.out ? { out: r(n.out) } : {}) })), closed };
    }
    case 'polyline': {
      const points = drawing.points.filter((p, i, all) => i === 0 || Math.hypot(p[0] - all[i - 1][0], p[1] - all[i - 1][1]) > 1e-4);
      if (points.length < 2) return null;
      const closed = points.length > 2 && Math.hypot(points[0][0] - points[points.length - 1][0], points[0][1] - points[points.length - 1][1]) < 1e-4;
      return { id, kind: 'polyline', points: (closed ? points.slice(0, -1) : points).map(r), closed };
    }
  }
}

/** Preview shape of an in-progress drawing with the cursor at `cursor`. */
function previewFromDrawing(drawing: Drawing, cursor: Vec2): ObjectShape | null {
  if (drawing.kind === 'polyline') return drawing.points.length > 0 ? { id: 'preview', kind: 'polyline', points: [...drawing.points, cursor], closed: false } : null;
  if (drawing.kind === 'pen') {
    // The placed nodes, plus a rubber-band segment to the cursor while no handle is being pulled.
    const nodes = drawing.drag ? drawing.nodes : [...drawing.nodes, { point: cursor }];
    return nodes.length > 1 ? { id: 'preview', kind: 'path', nodes, closed: drawing.closing } : null;
  }
  if (drawing.kind === 'arc' && !drawing.start) return { id: 'preview', kind: 'polyline', points: [drawing.center, cursor], closed: false };
  return shapeFromDrawing(drawing, cursor);
}

const Canvas2D_: React.FC = () => {
  const { store, ui, setUi } = useObjectEditor();
  const { spec: draftSpec, selection: draftSelection } = useEditorState();
  // A published version under preview (History tab) is shown read-only.
  const spec = ui.preview?.spec ?? draftSpec;
  const selection = ui.preview ? NO_SELECTION : draftSelection;
  /** The drawing as shown: formula coordinates resolved at the default size (index copies at i = 0). */
  const shapes = useMemo(() => resolveAuthoredShapes(spec), [spec]);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState<CanvasSize>({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [cursor, setCursor] = useState<{ point: Vec2; target: Vec2 | null } | null>(null);
  // A drawing belongs to the tool (and mode) it was started with: switching drops it without an effect.
  const [drawingState, setDrawingState] = useState<{ tool: string; drawing: Drawing } | null>(null);
  /** Shapes being dragged: left out of the snap candidates so they don't snap onto themselves. */
  const [draggedIds, setDraggedIds] = useState<readonly string[]>([]);
  const [marquee, setMarquee] = useState<Bounds | null>(null);
  /**
   * A footprint edge drag in progress: the previewed footprint in the frame the drag started in. Nothing is
   * re-centered until release — moving the origin (and the grid with it) mid-drag made the snapping jitter.
   * The footprint's own points are no snap targets meanwhile.
   */
  const [footprintPreview, setFootprintPreview] = useState<Bounds | null>(null);
  const footprintDrag = footprintPreview !== null;
  /**
   * A point the grid runs through. Starts at the origin; resizing the footprint from one edge re-centers the
   * drawing, and the grid moves along with it so it stays fixed on screen and under the drawing.
   */
  const [gridOrigin, setGridOrigin] = useState<Vec2>([0, 0]);
  const dragRef = useRef<Drag | null>(null);
  const spaceHeld = useRef(false);
  /** The right-click menu, at a canvas position (px). */
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const selectionActions = useSelectionActions();

  const testMode = ui.mode === 'test';
  const drawing = drawingState && drawingState.tool === ui.tool && !testMode ? drawingState.drawing : null;
  const setDrawing = useCallback((next: Drawing | null) => setDrawingState(next ? { tool: ui.tool, drawing: next } : null), [ui.tool]);
  const { width, depth } = testMode ? testSize(ui.test, spec) : spec.defaultSize;
  /** Doors / windows: no footprint — a wall with the opening, the drawing hinged at −u, opening toward +n. */
  const opening = spec.opening ?? null;
  const selectedIds = useMemo(() => (selection.kind === 'shapes' ? selection.ids : []), [selection]);
  const selectedGroup = selection.kind === 'group' ? spec.groups.find((g) => g.id === selection.id) : undefined;

  // Size tracking; the first measurement fits the footprint.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  // Fitted to the footprint the editor opened with — NOT refitted when the default size changes, or the
  // drawing would seem not to change at all (the view would zoom along). "Fit" refits on demand.
  const [openingSize] = useState(() => fitExtent(spec, spec.defaultSize.width, spec.defaultSize.depth));
  // Objects show their wall side on top (turned); doors / windows keep their wall across the middle.
  const turned = isTurnedView(spec);
  const view = useMemo(() => viewport ?? (size.width > 0 ? fitViewport(size, openingSize.width, openingSize.depth, turned) : null), [viewport, size, openingSize, turned]);

  const project = useCallback((p: Vec2): [number, number] => (view ? toScreen(view, size, p) : [0, 0]), [view, size]);
  const tolerance = view ? HIT_PX / view.scale : 0.01;

  // Space = temporary pan; Escape/Enter/Backspace drive drawings.
  useEffect(() => {
    const isTyping = (e: KeyboardEvent) => e.target instanceof HTMLElement && (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName));
    const down = (e: KeyboardEvent) => {
      if (isTyping(e)) return;
      if (e.code === 'Space') spaceHeld.current = true;
      if (!drawing) return;
      const finish = () => {
        const shape = finishedShape(drawing);
        if (shape) store.apply((s) => addShapes(s, [shape]), { kind: 'shapes', ids: [shape.id] });
        setDrawing(null);
      };
      // Handled here: the editor's own shortcuts (deselect, delete selection) must not fire too.
      if (e.key === 'Escape') {
        // The pen keeps what was drawn (like Illustrator); other tools drop the unfinished shape.
        if (drawing.kind === 'pen') finish();
        else setDrawing(null);
        e.stopPropagation();
      } else if (e.key === 'Enter' && (drawing.kind === 'polyline' || drawing.kind === 'pen')) {
        finish();
        e.stopPropagation();
      } else if (e.key === 'Backspace' && drawing.kind === 'polyline') {
        setDrawing(drawing.points.length > 1 ? { kind: 'polyline', points: drawing.points.slice(0, -1) } : null);
        e.preventDefault();
        e.stopPropagation();
      } else if (e.key === 'Backspace' && drawing.kind === 'pen') {
        setDrawing(drawing.nodes.length > 1 ? { ...drawing, nodes: drawing.nodes.slice(0, -1), drag: null } : null);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceHeld.current = false;
    };
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up);
    };
  }, [drawing, setDrawing, store]);

  // Wheel zoom must be non-passive to prevent page scroll.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      setViewport((current) => {
        const base = current ?? view;
        return base ? zoomAt(base, size, e.clientX - rect.left, e.clientY - rect.top, Math.exp(-e.deltaY * 0.0015)) : current;
      });
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [view, size]);

  const candidates = useMemo(
    // Doors / windows snap to the jambs' corners on the default wall instead of a footprint.
    () => (ui.snapToPoints ? snapCandidates(shapes, spec.defaultSize.width, spec.opening ? DEFAULT_OPENING_WALL_THICKNESS : spec.defaultSize.depth, new Set(draggedIds), !footprintDrag) : []),
    [shapes, spec.defaultSize.width, spec.defaultSize.depth, spec.opening, ui.snapToPoints, draggedIds, footprintDrag],
  );

  /** Grid/point snapping; Shift constrains to 45° steps, Ctrl/Cmd (`free`) turns snapping off. */
  const snap = useCallback(
    (p: Vec2, from?: Vec2, constrain?: boolean, free?: boolean) => {
      const constrained = constrain && from ? constrainAngle(from, p) : p;
      const off = constrain || free;
      return snapPoint(constrained, { grid: off ? 0 : ui.grid, gridOrigin, points: off ? [] : candidates, tolerance });
    },
    [ui.grid, gridOrigin, candidates, tolerance],
  );
  const noSnap = (e: { ctrlKey: boolean; metaKey: boolean }): boolean => e.ctrlKey || e.metaKey;

  const pointerWorld = (e: React.MouseEvent): Vec2 => {
    const rect = e.currentTarget.getBoundingClientRect();
    return view ? toWorld(view, size, e.clientX - rect.left, e.clientY - rect.top) : [0, 0];
  };

  const hitShape = useCallback(
    (p: Vec2): ObjectShape | null => {
      for (let i = shapes.length - 1; i >= 0; i--) {
        const shape = shapes[i];
        if (!shape.hidden && distanceToShape(shape, p) <= tolerance) return shape;
      }
      return null;
    },
    [shapes, tolerance],
  );

  const singleSelected = selectedIds.length === 1 ? shapes.find((s) => s.id === selectedIds[0]) : undefined;
  /** Points on a parameter or a custom formula follow it — they can't be dragged (pinned ones can). */
  const lockedNodes = useMemo(() => {
    if (!singleSelected?.expr) return new Set<number>();
    return new Set(shapeNodes(singleSelected).flatMap((_, i) => (nodeDraggable(spec, singleSelected.id, i) ? [] : [i])));
  }, [spec, singleSelected]);

  // Transform gizmo: on the selected shapes (or the selected group's), select tool only.
  const gizmoIds = useMemo(() => (selectedIds.length > 0 ? selectedIds : (selectedGroup?.shapeIds ?? [])), [selectedIds, selectedGroup]);
  const gizmoEnabled = ui.tool === 'select' && !testMode && !ui.preview && gizmoIds.length > 0;
  /** Test mode: the stand-in object gets the gizmo a placed object gets. */
  const testGizmo = testMode && !ui.preview && !opening;
  const handles = useMemo(() => (testMode && !opening ? testHandles(spec, ui.test) : []), [testMode, opening, spec, ui.test]);
  const jambHandles = useMemo(() => (testMode && opening ? testOpeningHandles(spec, ui.test) : []), [testMode, opening, spec, ui.test]);
  const bendHandle = useMemo(() => (testMode && opening ? testWallBendHandle(spec, ui.test) : null), [testMode, opening, spec, ui.test]);
  const [overlay, setOverlay] = useState<GizmoOverlay | null>(null);
  const [gizmoHover, setGizmoHover] = useState(false);
  const overlayCleanup = useRef<(() => void) | null>(null);
  // three.js lives outside React: built when the layer's element mounts, disposed when it unmounts.
  const overlayRef = useCallback(
    (element: HTMLDivElement | null) => {
      overlayCleanup.current?.();
      overlayCleanup.current = null;
      if (!element) {
        setOverlay(null);
        return;
      }
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.style.display = 'block';
      element.appendChild(renderer.domElement);
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
      const gizmo = new TransformGizmo({ getCamera: () => camera, getDomElement: () => renderer.domElement });
      setOverlay({ renderer, camera, gizmo, binding: new ShapesGizmoBinding(store), testBinding: new TestInstanceGizmoBinding((test) => setUi((u) => ({ ...u, test }))) });
      overlayCleanup.current = () => {
        gizmo.dispose();
        renderer.dispose();
        renderer.domElement.remove();
      };
    },
    [store, setUi],
  );
  useEffect(() => {
    if (!overlay || !view || size.width === 0) return;
    overlay.binding.configure(gizmoIds, ui.grid, gridOrigin);
    overlay.testBinding.configure(spec, ui.test);
    overlay.gizmo.setBinding(testGizmo ? overlay.testBinding : gizmoEnabled ? overlay.binding : null);
    drawGizmoOverlay(overlay, view, size);
  }, [overlay, view, size, gizmoIds, gizmoEnabled, testGizmo, ui.test, ui.grid, gridOrigin, spec]);

  /** Pixel position for the gizmo (same box as the SVG and the overlay canvas). */
  const pointerPixel = (e: React.PointerEvent): THREE.Vector2 => {
    const rect = e.currentTarget.getBoundingClientRect();
    return new THREE.Vector2(e.clientX - rect.left, e.clientY - rect.top);
  };

  const hitNode = (p: Vec2): number => {
    if (!singleSelected || ui.tool !== 'node') return -1;
    return shapeNodes(singleSelected).findIndex((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) <= tolerance);
  };

  const hitPathHandle = (p: Vec2): { index: number; side: PathHandleSide } | null => {
    if (singleSelected?.kind !== 'path' || ui.tool !== 'node' || testMode || ui.preview) return null;
    return pathHandles(singleSelected).find((h) => Math.hypot(h.point[0] - p[0], h.point[1] - p[1]) <= tolerance) ?? null;
  };

  const hitTestHandle = (p: Vec2): TestHandle | null => handles.find((h) => Math.hypot(h.u - p[0], h.n - p[1]) <= tolerance * 1.5) ?? null;
  const hitJamb = (p: Vec2): boolean => jambHandles.some((h) => Math.hypot(h.u - p[0], h.n - p[1]) <= tolerance * 1.5);
  const hitBend = (p: Vec2): boolean => !!bendHandle && Math.hypot(bendHandle[0] - p[0], bendHandle[1] - p[1]) <= tolerance * 1.5;

  // Footprint edge handles: edit mode, select tool. Resizing there never scales the drawing.
  const footprintEditable = ui.tool === 'select' && !testMode && !ui.preview;
  // Doors / windows: only the opening width (at the jambs), always about the center.
  const footprintEdges = opening ? FOOTPRINT_EDGES.filter(({ edge }) => edge[0] === 'u') : FOOTPRINT_EDGES;
  const hitFootprintEdge = (p: Vec2): FootprintEdge | null => {
    if (!footprintEditable) return null;
    const hit = footprintEdges.find(({ at }) => Math.hypot(at[0] * width - p[0], at[1] * depth - p[1]) <= tolerance * 1.5);
    return hit?.edge ?? null;
  };

  const commitDrawing = (shape: ObjectShape | null) => {
    if (shape) store.apply((s) => addShapes(s, [shape]), { kind: 'shapes', ids: [shape.id] });
    setDrawing(null);
  };

  // ─── Pointer handling ─────────────────────────────────────────────

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!view) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const world = pointerWorld(e);

    if (e.button === 1 || spaceHeld.current || ui.tool === 'pan') {
      dragRef.current = { kind: 'pan', x: e.clientX, y: e.clientY, viewport: view };
      return;
    }
    // Right-click, like on an object in a project: select what is under the cursor (a selection holding it stays) and
    // show its actions; on empty space, clear the selection.
    if (e.button === 2) {
      // While drawing a polyline or curve: finish it like Enter (the cursor's spot is not added).
      if (drawing?.kind === 'polyline' || drawing?.kind === 'pen') {
        commitDrawing(finishedShape(drawing));
        return;
      }
      if (testMode || ui.preview || drawing) return;
      const hit = hitShape(world);
      if (!hit) {
        store.select({ kind: 'none' });
        setMenu(null);
        return;
      }
      const current = store.getState().selection;
      const held = (current.kind === 'shapes' && current.ids.includes(hit.id)) || (current.kind === 'group' && !!spec.groups.find((g) => g.id === current.id)?.shapeIds.includes(hit.id));
      if (!held) store.select({ kind: 'shapes', ids: [hit.id] });
      const rect = e.currentTarget.getBoundingClientRect();
      setMenu({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      return;
    }
    if (e.button !== 0) return;

    if (ui.preview) return;
    if (testMode) {
      if (opening) {
        if (hitJamb(world)) dragRef.current = { kind: 'testJamb', before: ui.test };
        else if (hitBend(world)) dragRef.current = { kind: 'testBend', before: ui.test };
        return;
      }
      // Like a placed object: its resize handles first, then the gizmo.
      const handle = hitTestHandle(world);
      if (handle) {
        dragRef.current = { kind: 'test', handle, before: ui.test };
        return;
      }
      if (overlay?.gizmo.getBinding()) {
        const px = pointerPixel(e);
        const gizmoHandle = overlay.gizmo.updateActiveHandle(px);
        const worldPoint = gizmoHandle ? overlay.gizmo.getHandleIntersection(gizmoHandle, px) : null;
        if (gizmoHandle && worldPoint && overlay.gizmo.startDrag(gizmoHandle, px, worldPoint)) dragRef.current = { kind: 'gizmo' };
      }
      return;
    }

    if (DRAWING_TOOLS.includes(ui.tool)) {
      const last = drawing?.kind === 'polyline' || drawing?.kind === 'arc' || drawing?.kind === 'pen' ? lastDrawingPoint(drawing) : undefined;
      const p = snap(world, last, e.shiftKey, noSnap(e)).point;
      const press = { x: e.clientX, y: e.clientY };
      if (!drawing) {
        if (ui.tool === 'polyline') setDrawing({ kind: 'polyline', points: [p] });
        else if (ui.tool === 'pen') setDrawing({ kind: 'pen', nodes: [{ point: p }], drag: { index: 0, ...press }, closing: false });
        else if (ui.tool === 'arc') setDrawing({ kind: 'arc', center: p, start: null });
        else setDrawing({ kind: ui.tool === 'line' ? 'line' : ui.tool === 'rect' ? 'rect' : 'circle', start: p, pressed: true });
        return;
      }
      if (drawing.kind === 'polyline') {
        // A click on the first point closes the outline.
        const first = drawing.points[0];
        if (drawing.points.length > 2 && Math.hypot(p[0] - first[0], p[1] - first[1]) <= tolerance) commitDrawing(shapeFromDrawing({ kind: 'polyline', points: [...drawing.points, first] }, first));
        else setDrawing({ kind: 'polyline', points: [...drawing.points, p] });
        return;
      }
      if (drawing.kind === 'pen') {
        const first = drawing.nodes[0];
        const lastNode = drawing.nodes[drawing.nodes.length - 1];
        const near = (q: Vec2) => Math.hypot(world[0] - q[0], world[1] - q[1]) <= tolerance;
        // On the first node: close (a drag there rounds the closing point).
        if (drawing.nodes.length > 2 && near(first.point)) setDrawing({ ...drawing, closing: true, drag: { index: 0, ...press } });
        // On the last node: drop its out handle, so the next segment leaves it sharply.
        else if (near(lastNode.point)) setDrawing({ ...drawing, nodes: drawing.nodes.map((n, i) => (i === drawing.nodes.length - 1 ? { point: n.point, ...(n.in ? { in: n.in } : {}) } : n)), drag: null });
        else setDrawing({ ...drawing, nodes: [...drawing.nodes, { point: p }], drag: { index: drawing.nodes.length, ...press } });
        return;
      }
      if (drawing.kind === 'arc') {
        if (!drawing.start) setDrawing({ ...drawing, start: p });
        else commitDrawing(shapeFromDrawing(drawing, p));
        return;
      }
      // Second click of a click-click line/rect/circle.
      commitDrawing(shapeFromDrawing(drawing, p));
      return;
    }

    // Point tool: a curve's bezier handles, then the nodes of the selected shape; a click on a shape selects it.
    if (ui.tool === 'node') {
      const handle = hitPathHandle(world);
      if (singleSelected?.kind === 'path' && handle) {
        store.begin();
        setDraggedIds([singleSelected.id]);
        dragRef.current = { kind: 'pathHandle', id: singleSelected.id, index: handle.index, side: handle.side, base: singleSelected };
        return;
      }
      const nodeIndex = hitNode(world);
      if (singleSelected && nodeIndex >= 0) {
        // Picks the point (its pin rules show in the inspector); a draggable one moves too.
        setUi((u) => ({ ...u, node: { shapeId: singleSelected.id, index: nodeIndex } }));
        if (lockedNodes.has(nodeIndex)) return;
        store.begin();
        setDraggedIds([singleSelected.id]);
        dragRef.current = { kind: 'node', id: singleSelected.id, index: nodeIndex, base: singleSelected, spec: store.getState().spec };
        return;
      }
      const hit = hitShape(world);
      if (hit) {
        store.select({ kind: 'shapes', ids: [hit.id] });
        return;
      }
      dragRef.current = { kind: 'marquee', start: world, additive: e.shiftKey };
      if (!e.shiftKey) store.select({ kind: 'none' });
      return;
    }

    // Select tool: the gizmo, the footprint's edge handles, then picking / moving shapes.
    if (overlay?.gizmo.getBinding()) {
      const px = pointerPixel(e);
      const handle = overlay.gizmo.updateActiveHandle(px);
      const worldPoint = handle ? overlay.gizmo.getHandleIntersection(handle, px) : null;
      if (handle && worldPoint && overlay.gizmo.startDrag(handle, px, worldPoint)) {
        setDraggedIds(gizmoIds);
        dragRef.current = { kind: 'gizmo' };
        return;
      }
    }
    const edge = hitFootprintEdge(world);
    if (edge) {
      setFootprintPreview({ min: [-width / 2, -depth / 2], max: [width / 2, depth / 2] });
      dragRef.current = { kind: 'footprint', edge, position: null, symmetric: false };
      return;
    }
    const hit = hitShape(world);
    if (hit) {
      const already = selectedIds.includes(hit.id);
      const ids = e.shiftKey ? (already ? selectedIds.filter((id) => id !== hit.id) : [...selectedIds, hit.id]) : already ? selectedIds : [hit.id];
      store.select(ids.length ? { kind: 'shapes', ids } : { kind: 'none' });
      if (ids.includes(hit.id)) {
        store.begin();
        setDraggedIds(ids);
        dragRef.current = { kind: 'move', ids, base: store.getState().spec, grab: snap(world, undefined, false, noSnap(e)).point, x: e.clientX, y: e.clientY, moved: false };
      }
      return;
    }
    dragRef.current = { kind: 'marquee', start: world, additive: e.shiftKey };
    if (!e.shiftKey) store.select({ kind: 'none' });
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!view) return;
    const world = pointerWorld(e);
    const drag = dragRef.current;

    if (drag?.kind === 'pan') {
      setViewport(panBy(drag.viewport, e.clientX - drag.x, e.clientY - drag.y));
      return;
    }
    if (drag?.kind === 'test') {
      const { handle, before } = drag;
      const test = handle.kind === 'depth' ? dragTestDepthHandle(spec, before, handle.index, world[1]) : dragTestRunEnd(spec, before, handle.kind, world[0]);
      setUi((u) => ({ ...u, test }));
      return;
    }
    if (drag?.kind === 'testJamb') {
      const test = dragTestOpeningJamb(spec, drag.before, world);
      setUi((u) => ({ ...u, test }));
      return;
    }
    if (drag?.kind === 'testBend') {
      const test = dragTestWallBend(drag.before, world);
      setUi((u) => ({ ...u, test }));
      return;
    }
    if (drag?.kind === 'gizmo') {
      // Shift or Ctrl/Cmd: no grid / angle snapping.
      overlay?.gizmo.processDrag(pointerPixel(e), { override: e.shiftKey || e.ctrlKey || e.metaKey });
      return;
    }
    if (drag?.kind === 'footprint') {
      // Snaps to the grid and the drawing's points (Ctrl/Cmd: free); Alt resizes around the center.
      const result = snap(world, undefined, false, noSnap(e));
      setCursor(result);
      const u = drag.edge[0] === 'u';
      drag.position = u ? result.point[0] : result.point[1];
      drag.symmetric = e.altKey || !!opening;
      const { size, center } = footprintEdgeResize(spec, drag.edge, drag.position, drag.symmetric);
      setFootprintPreview(
        u ? { min: [center - size / 2, -depth / 2], max: [center + size / 2, depth / 2] } : { min: [-width / 2, center - size / 2], max: [width / 2, center + size / 2] },
      );
      return;
    }
    if (drag?.kind === 'pathHandle') {
      // Snapped like everything else; Alt breaks a smooth node's handles apart.
      const result = snap(world, undefined, false, noSnap(e));
      setCursor(result);
      store.apply(() => updateShape(store.getState().spec, drag.id, () => movePathHandle(drag.base, drag.index, drag.side, result.point, e.altKey)));
      return;
    }
    if (drag?.kind === 'node') {
      const result = snap(world, undefined, false, noSnap(e));
      setCursor(result);
      // Formula-driven shapes keep their pins: the dragged point takes a new distance from its edge.
      const formulaDriven = drag.spec.shapes.find((s) => s.id === drag.id)?.expr !== undefined;
      store.apply(() =>
        formulaDriven ? dragPinnedNode(drag.spec, drag.id, drag.index, result.point) : updateShape(store.getState().spec, drag.id, () => moveShapeNode(drag.base, drag.index, result.point)),
      );
      return;
    }
    if (drag?.kind === 'move') {
      if (!drag.moved && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < DRAG_PX) return;
      drag.moved = true;
      const result = snap(world, undefined, false, noSnap(e));
      setCursor(result);
      const du = result.point[0] - drag.grab[0];
      const dn = result.point[1] - drag.grab[1];
      store.apply(() => translateShapes(drag.base, drag.ids, du, dn));
      return;
    }
    if (drag?.kind === 'marquee') {
      setMarquee({ min: [Math.min(drag.start[0], world[0]), Math.min(drag.start[1], world[1])], max: [Math.max(drag.start[0], world[0]), Math.max(drag.start[1], world[1])] });
      return;
    }

    if (overlay && view && (gizmoEnabled || testGizmo) && !drag) {
      const hovered = overlay.gizmo.updateActiveHandle(pointerPixel(e)) !== null;
      // Hover highlight changed: redraw the layer.
      drawGizmoOverlay(overlay, view, size);
      if (hovered !== gizmoHover) setGizmoHover(hovered);
    }
    // Pen: pulling a handle out of the node just placed (Shift: 45° steps, Alt: only the outgoing handle).
    if (drawing?.kind === 'pen' && drawing.drag) {
      const { index, x, y } = drawing.drag;
      const node = drawing.nodes[index];
      if (!node || Math.hypot(e.clientX - x, e.clientY - y) < DRAG_PX) return;
      const result = snap(world, node.point, e.shiftKey, noSnap(e));
      setCursor(result);
      const out = result.point;
      const pulled: ObjectPathNode = drawing.closing
        ? // Closing on the first node: the pull rounds the incoming side (and, unless Alt, the outgoing one along).
          { point: node.point, in: mirrorPoint(node.point, out), ...(e.altKey ? (node.out ? { out: node.out } : {}) : { out }) }
        : { point: node.point, ...(e.altKey ? (node.in ? { in: node.in } : {}) : { in: mirrorPoint(node.point, out) }), out };
      setDrawing({ ...drawing, nodes: drawing.nodes.map((n, i) => (i === index ? pulled : n)) });
      return;
    }
    const last = lastDrawingPoint(drawing);
    setCursor(DRAWING_TOOLS.includes(ui.tool) && !testMode ? snap(world, last, e.shiftKey, noSnap(e)) : { point: world, target: null });
  };

  const onPointerUp = (e: React.PointerEvent<SVGSVGElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (drag?.kind === 'move' || drag?.kind === 'node' || drag?.kind === 'pathHandle') {
      store.commit();
      setDraggedIds([]);
    }
    if (drag?.kind === 'footprint') {
      setFootprintPreview(null);
      if (drag.position !== null && view) {
        // One step: resize + re-center the drawing, and pan along by the same shift so nothing moves on screen.
        const next = moveFootprintEdge(spec, drag.edge, drag.position, drag.symmetric);
        store.apply(() => next.spec);
        setViewport(drag.edge[0] === 'u' ? { ...view, cu: view.cu - next.shift } : { ...view, cn: view.cn - next.shift });
        setGridOrigin(([ou, on]) => (drag.edge[0] === 'u' ? [roundCoord(ou - next.shift), on] : [ou, roundCoord(on - next.shift)]));
      }
    }
    if (drag?.kind === 'gizmo' && overlay) {
      const binding = testGizmo ? overlay.testBinding : overlay.binding;
      if (overlay.gizmo.endDrag()) binding.commitDrag();
      else binding.cancelDrag();
      setDraggedIds([]);
    }
    if (drag?.kind === 'marquee') {
      const box = marquee;
      setMarquee(null);
      if (box && view && (box.max[0] - box.min[0]) * view.scale > DRAG_PX) {
        const hits = shapes.filter((s) => !s.hidden && boundsIntersect(shapeBounds(s), box)).map((s) => s.id);
        const ids = drag.additive ? [...new Set([...selectedIds, ...hits])] : hits;
        store.select(ids.length ? { kind: 'shapes', ids } : { kind: 'none' });
      }
    }
    // Pen: the handle pull ends; a press on the first node closes the path.
    if (drawing?.kind === 'pen' && drawing.drag) {
      if (drawing.closing) commitDrawing(shapeFromDrawing(drawing, drawing.nodes[0].point));
      else setDrawing({ ...drawing, drag: null });
    }
    // Drag-drawn line/rect/circle: releasing away from the start finishes it.
    if (drawing && 'pressed' in drawing && drawing.pressed && view) {
      const end = snap(pointerWorld(e), drawing.start, e.shiftKey, noSnap(e)).point;
      const [sx, sy] = toScreen(view, size, drawing.start);
      const [ex, ey] = toScreen(view, size, end);
      if (Math.hypot(ex - sx, ey - sy) > DRAG_PX * 2) commitDrawing(shapeFromDrawing(drawing, end));
      else setDrawing({ ...drawing, pressed: false });
    }
  };

  const onDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (drawing?.kind === 'polyline') commitDrawing(shapeFromDrawing(drawing, drawing.points[drawing.points.length - 1]));
    if (drawing?.kind === 'pen') commitDrawing(shapeFromDrawing(drawing, drawing.nodes[0].point));
    if (drawing || testMode || ui.preview) return;
    // Select tool: double-clicking a shape edits its points.
    if (ui.tool === 'select') {
      const hit = hitShape(pointerWorld(e));
      if (hit) {
        store.select({ kind: 'shapes', ids: [hit.id] });
        setUi((u) => ({ ...u, tool: 'node' }));
      }
      return;
    }
    // Point tool on a curve's point: switch it between sharp and smooth.
    if (ui.tool === 'node' && singleSelected?.kind === 'path') {
      const index = hitNode(pointerWorld(e));
      if (index >= 0) store.apply((s) => updateShape(s, singleSelected.id, () => togglePathNodeSmooth(singleSelected, index)));
    }
  };

  // ─── Rendering ────────────────────────────────────────────────────

  /**
   * The evaluated glyph: the faint underlay while editing, the object itself in Test mode. Dashed lines are kept apart
   * and dashed in screen pixels like the authored shapes, so a dashed shape, its repeat copies and its Test-mode
   * look all match.
   */
  const evaluated = useMemo((): { solid: number[]; dashed: number[] } => {
    const dashed: number[] = [];
    if (testMode) return { solid: spec.opening ? testOpeningGlyph(spec, ui.test, 'glyph', dashed) : evaluateCustomGlyph(spec, testFrame(spec, ui.test), ui.test.params, 'glyph', { dashedOut: dashed, offset: dimensionOffsetOf(ui.test.params) }), dashed };
    return { solid: evaluateCustomGlyph(spec, originFrame(width, depth), {}, 'glyph', { dashedOut: dashed }), dashed };
  }, [spec, width, depth, testMode, ui.test]);
  const selectionGlyph = useMemo(
    () => (testMode ? (spec.opening ? testOpeningGlyph(spec, ui.test, 'selection') : evaluateCustomGlyph(spec, testFrame(spec, ui.test), ui.test.params, 'selection', { offset: dimensionOffsetOf(ui.test.params) })) : []),
    [spec, testMode, ui.test],
  );
  const openingGuides = useMemo(() => (spec.opening ? (testMode ? { wall: testWallPieces(spec, ui.test), hinge: null, side: null } : openingEditGuides(spec)) : null), [spec, testMode, ui.test]);

  const menuItems: CanvasContextMenuItem[] = selectionActions.map((a) => ({ id: a.id, icon: a.icon, label: a.shortcut ? `${a.label} (${a.shortcut})` : a.label, onClick: a.run, danger: a.danger }));

  if (!view || size.width === 0) return <div ref={containerRef} className="h-full w-full" />;

  const { minor, major } = gridSteps(view.scale);
  const shown = visibleWorld(view, size);
  const screenSign = view.turned ? -1 : 1;
  const gridLines: Array<{ key: string; d: string; major: boolean }> = [];
  // Lines at gridOrigin + k·minor; k counts from the grid origin so major lines stay put too.
  const isMajor = (k: number): boolean => Math.abs((k * minor) / major - Math.round((k * minor) / major)) < 1e-6;
  for (let k = Math.ceil((shown.min[0] - gridOrigin[0]) / minor); gridOrigin[0] + k * minor <= shown.max[0]; k++) {
    const x = project([gridOrigin[0] + k * minor, 0])[0];
    gridLines.push({ key: `u${k}`, d: `M${x.toFixed(1)} 0V${size.height}`, major: isMajor(k) });
  }
  for (let k = Math.ceil((shown.min[1] - gridOrigin[1]) / minor); gridOrigin[1] + k * minor <= shown.max[1]; k++) {
    const y = project([0, gridOrigin[1] + k * minor])[1];
    gridLines.push({ key: `n${k}`, d: `M0 ${y.toFixed(1)}H${size.width}`, major: isMajor(k) });
  }

  // The footprint: centered on the origin, or the previewed one while an edge is dragged.
  // Test mode: around the test object wherever it was moved to.
  const [tcu, tcn] = testMode ? testCenter(ui.test) : [0, 0];
  const footprint = footprintPreview ?? { min: [tcu - width / 2, tcn - depth / 2], max: [tcu + width / 2, tcn + depth / 2] };
  // Its screen box (sorted: a turned view swaps the corners); the wall side (−n) is the bottom edge, or the top turned.
  const [minX, minY] = project(footprint.min);
  const [maxX, maxY] = project(footprint.max);
  const [fx0, fx1, fy0, fy1] = [Math.min(minX, maxX), Math.max(minX, maxX), Math.min(minY, maxY), Math.max(minY, maxY)];
  const [ox, oy] = project([0, 0]);

  /** A path node's bezier handles: a thin line from the anchor to a small round grip. */
  const handleMarks = (node: ObjectPathNode, key: string): React.ReactNode[] =>
    (['in', 'out'] as const).flatMap((side) => {
      const h = node[side];
      if (!h) return [];
      const [ax, ay] = project(node.point);
      const [hx, hy] = project(h);
      return [<path key={`${key}${side}l`} d={`M${ax} ${ay}L${hx} ${hy}`} className="stroke-french-violet-600/70" strokeWidth={1} />, <circle key={`${key}${side}c`} cx={hx} cy={hy} r={4} className="fill-white stroke-french-violet-600" strokeWidth={1.5} />];
    });
  const shapePath = (shape: ObjectShape): string => tessellateObjectShape(shape).map((poly) => polylinePath(poly.points, project)).join(' ');
  const preview = drawing && cursor ? previewFromDrawing(drawing, cursor.point) : null;
  const isDrawingTool = DRAWING_TOOLS.includes(ui.tool) && !testMode;

  const cellBand = (() => {
    if (!selectedGroup?.repeat || selectedGroup.repeat.kind === 'index' || testMode) return null;
    if (selectedGroup.repeat.kind === 'radial') {
      const [cx, cy] = project(selectedGroup.repeat.center);
      return <circle cx={cx} cy={cy} r={6} className="fill-french-violet-600/30 stroke-french-violet-600" strokeWidth={1.5} />;
    }
    const [a, b] = selectedGroup.repeat.cell;
    const hd = spec.defaultSize.depth / 2;
    const hw = spec.defaultSize.width / 2;
    const [x0, y0] = project(selectedGroup.repeat.axis === 'u' ? [a, hd] : [-hw, b]);
    const [x1, y1] = project(selectedGroup.repeat.axis === 'u' ? [b, -hd] : [hw, a]);
    return <rect x={Math.min(x0, x1)} y={Math.min(y0, y1)} width={Math.abs(x1 - x0)} height={Math.abs(y1 - y0)} className="fill-french-violet-600/10 stroke-french-violet-600/60" strokeDasharray="4 3" />;
  })();

  // Where a placed object's resize handles sit at the default size (fractions or formulas).
  const pickedPins = (() => {
    const node = ui.node;
    if (testMode || ui.preview || ui.tool !== 'node' || !node || !singleSelected || singleSelected.id !== node.shapeId) return null;
    const p = shapeNodes(singleSelected)[node.index];
    if (!p) return null;
    const hw = spec.defaultSize.width / 2;
    const hd = spec.defaultSize.depth / 2;
    return (['u', 'n'] as const).map((axis) => {
      const state = pinStateOf(spec, node.shapeId, node.index, axis);
      if (!state || state.mode === 'custom' || state.mode === 'stretch') return null;
      const from = state.mode === 'start' ? -1 : state.mode === 'end' ? 1 : 0;
      const a: Vec2 = axis === 'u' ? [from * hw, p[1]] : [p[0], from * hd];
      const [x0, y0] = project(a);
      const [x1, y1] = project(p);
      const param = typeof state.distance === 'string' ? spec.params.find((q) => q.key === state.distance) : undefined;
      const text = param ? param.label : formatLength(Math.abs(typeof state.distance === 'number' ? state.distance : 0));
      return (
        <g key={axis} className="pointer-events-none">
          <path d={`M${x0} ${y0}L${x1} ${y1}`} className="stroke-sky-600 dark:stroke-sky-400" strokeWidth={1.25} strokeDasharray="4 3" />
          <path d={axis === 'u' ? `M${x0} ${y0 - 5}V${y0 + 5}` : `M${x0 - 5} ${y0}H${x0 + 5}`} className="stroke-sky-600 dark:stroke-sky-400" strokeWidth={1.25} />
          <text x={(x0 + x1) / 2} y={(y0 + y1) / 2 - 6} textAnchor="middle" className={classNames('text-[11px]', param ? 'fill-sky-700 font-semibold dark:fill-sky-300' : 'fill-sky-700 dark:fill-sky-300')}>
            {text}
          </text>
        </g>
      );
    });
  })();

  const handleMarkers: Vec2[] = (() => {
    const layout = customHandleLayout('preview', spec)?.(originFrame(spec.defaultSize.width, spec.defaultSize.depth), defaultCustomParams(spec));
    if (!layout) return [];
    return [...(layout.start ? [layout.start] : []), ...(layout.end ? [layout.end] : []), ...(layout.depth ?? []).map((d): Vec2 => [d.u, d.n])];
  })();

  return (
    <div ref={containerRef} className="relative h-full w-full select-none overflow-hidden bg-[#f4f5f5] dark:bg-[#262626]">
      <svg
        ref={svgRef}
        width={size.width}
        height={size.height}
        className={classNames('block touch-none', ui.tool === 'pan' ? 'cursor-grab' : (gizmoEnabled || testGizmo) && gizmoHover ? 'cursor-pointer' : isDrawingTool ? 'cursor-crosshair' : 'cursor-default')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={() => setCursor(null)}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        <g>
          {gridLines.map((line) => (
            <path key={line.key} d={line.d} className={line.major ? 'stroke-gray-300 dark:stroke-white/15' : 'stroke-gray-200 dark:stroke-white/5'} strokeWidth={1} />
          ))}
        </g>

        {openingGuides ? (
          /* Door / window: the wall around the opening (the test wall in Test mode), the hinge jamb and the opening side */
          <g className="pointer-events-none">
            {openingGuides.wall.map((piece, i) => (
              <path key={i} d={`${polylinePath(piece, project)}Z`} className="fill-gray-300/80 stroke-gray-500 dark:fill-white/15 dark:stroke-gray-400" strokeWidth={1} />
            ))}
            <path d={`M${ox - 6} ${oy}H${ox + 6}M${ox} ${oy - 6}V${oy + 6}`} className="stroke-gray-400" />
            {(() => {
              // The previewed width while a jamb is dragged.
              const [x0, y0] = project([footprint.min[0], 0]);
              const [x1] = project([footprint.max[0], 0]);
              return (
                <text x={(x0 + x1) / 2} y={y0 + 22} textAnchor="middle" className="fill-gray-500 text-[11px] dark:fill-gray-400">
                  {formatLength(footprint.max[0] - footprint.min[0])}
                </text>
              );
            })()}
            {openingGuides.hinge &&
              (() => {
                const [hx, hy] = project(openingGuides.hinge);
                return (
                  <g>
                    <circle cx={hx} cy={hy} r={4} className="fill-sky-600 dark:fill-sky-400" />
                    <text x={hx} y={hy + 36} textAnchor="middle" className="fill-sky-700 text-[11px] dark:fill-sky-300">
                      {opening?.kind === 'WINDOW' ? <T _str="start" /> : <T _str="hinge side" />}
                    </text>
                  </g>
                );
              })()}
            {openingGuides.side &&
              (() => {
                const [sx, sy] = project(openingGuides.side);
                return (
                  <g>
                    <path d={`M${sx} ${sy - 4}V${sy - 28}M${sx - 5} ${sy - 22}L${sx} ${sy - 28}L${sx + 5} ${sy - 22}`} className="stroke-sky-600 dark:stroke-sky-400" strokeWidth={1.5} fill="none" />
                    <text x={sx + 8} y={sy - 18} className="fill-sky-700 text-[11px] dark:fill-sky-300">
                      {opening?.kind === 'WINDOW' ? <T _str="outside" /> : <T _str="opens to" />}
                    </text>
                  </g>
                );
              })()}
          </g>
        ) : (
          <>
            {/* Footprint (default size in Edit mode, the test instance in Test mode) */}
            <rect x={fx0} y={fy0} width={fx1 - fx0} height={fy1 - fy0} className="fill-white/60 stroke-gray-400 dark:fill-white/5 dark:stroke-gray-500" strokeDasharray="6 4" />
            <path d={`M${ox - 6} ${oy}H${ox + 6}M${ox} ${oy - 6}V${oy + 6}`} className="stroke-gray-400" />
            {/* The width on the side away from the wall. */}
            <text x={(fx0 + fx1) / 2} y={view.turned ? fy1 + 16 : fy0 - 8} textAnchor="middle" className="fill-gray-500 text-[11px] dark:fill-gray-400">
              {formatLength(footprint.max[0] - footprint.min[0])}
            </text>
            <text x={fx1 + 8} y={(fy0 + fy1) / 2} dominantBaseline="middle" className="fill-gray-500 text-[11px] dark:fill-gray-400">
              {formatLength(footprint.max[1] - footprint.min[1])}
            </text>
          </>
        )}
        {spec.constraint.kind === 'WALL' && !opening && (
          <g>
            <path d={`M${fx0} ${view.turned ? fy0 - 4 : fy1 + 4}H${fx1}`} className="stroke-gray-500 dark:stroke-gray-300" strokeWidth={4} />
            <text x={(fx0 + fx1) / 2} y={view.turned ? fy0 - 12 : fy1 + 20} textAnchor="middle" className="fill-gray-500 text-[11px] dark:fill-gray-400">
              <T _str="wall side" />
            </text>
          </g>
        )}

        {cellBand}

        {/* Evaluated glyph: faint underlay while editing, the object itself in Test mode */}
        <path d={segmentsPath(evaluated.solid, project)} className={testMode ? 'stroke-gray-900 dark:stroke-white' : 'stroke-gray-300 dark:stroke-gray-600'} strokeWidth={testMode ? 2 : 1.5} fill="none" strokeLinecap="round" />
        {evaluated.dashed.length > 0 && (
          <path d={chainedSegmentsPath(evaluated.dashed, project)} className={testMode ? 'stroke-gray-900 dark:stroke-white' : 'stroke-gray-300 dark:stroke-gray-600'} strokeWidth={testMode ? 2 : 1.5} strokeDasharray="8 5" fill="none" />
        )}
        {testMode && <path d={segmentsPath(selectionGlyph, project)} className="stroke-french-violet-600" strokeWidth={1.5} fill="none" strokeDasharray="5 4" />}
        {/* A dimension's label, as a placed one shows it: the measured length above the middle of the line. */}
        {testMode && spec.dimension && (
          <text
            x={project([tcu, tcn + dimensionOffsetOf(ui.test.params)])[0]}
            y={project([tcu, tcn + dimensionOffsetOf(ui.test.params)])[1] - 10}
            textAnchor="middle"
            className="pointer-events-none fill-gray-900 text-[12px] font-semibold dark:fill-white"
          >
            {`${width.toFixed(2)} m`}
          </text>
        )}

        {/* Authored shapes (editable) */}
        {!testMode &&
          shapes.map((shape) =>
            shape.hidden ? null : (
              <path
                key={shape.id}
                d={shapePath(shape)}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={selectedIds.includes(shape.id) || selectedGroup?.shapeIds.includes(shape.id) ? 2.5 : 1.75}
                strokeDasharray={shape.style === 'dashed' ? '8 5' : undefined}
                className={classNames(
                  selectedIds.includes(shape.id) || selectedGroup?.shapeIds.includes(shape.id) ? 'stroke-french-violet-600' : shape.layer === 'selection' ? 'stroke-sky-600 dark:stroke-sky-400' : 'stroke-gray-900 dark:stroke-white',
                )}
              />
            ),
          )}

        {/* Footprint edge handles: resize the default size without scaling the drawing (Alt: around the center) */}
        {footprintEditable &&
          footprintEdges.map(({ edge, at, cursor }) => {
            const fu = footprint.max[0] - footprint.min[0];
            const fn = footprint.max[1] - footprint.min[1];
            const [x, y] = project([(footprint.min[0] + footprint.max[0]) / 2 + at[0] * fu, (footprint.min[1] + footprint.max[1]) / 2 + at[1] * fn]);
            const horizontal = edge[0] === 'u';
            return <rect key={edge} x={x - (horizontal ? 4 : 9)} y={y - (horizontal ? 9 : 4)} width={horizontal ? 8 : 18} height={horizontal ? 18 : 8} rx={2} className={classNames('fill-white stroke-gray-500 dark:fill-gray-700 dark:stroke-gray-300', cursor)} strokeWidth={1.5} />;
          })}

        {/* Bezier handles of a selected curve */}
        {!testMode && !ui.preview && ui.tool === 'node' && singleSelected?.kind === 'path' && <g>{singleSelected.nodes.flatMap((n, i) => handleMarks(n, `h${i}`))}</g>}

        {/* Nodes of a single selected shape (point tool) */}
        {!testMode &&
          !ui.preview &&
          ui.tool === 'node' &&
          singleSelected &&
          shapeNodes(singleSelected).map((p, i) => {
            const [x, y] = project(p);
            // Formula-driven points: shown, not draggable (a filled dot instead of a grip).
            const picked = ui.node?.shapeId === singleSelected.id && ui.node.index === i;
            if (picked) return <rect key={i} x={x - 5} y={y - 5} width={10} height={10} className="fill-french-violet-600 stroke-white" strokeWidth={1.5} />;
            return lockedNodes.has(i) ? (
              <circle key={i} cx={x} cy={y} r={3} className="fill-french-violet-600/60" />
            ) : (
              <rect key={i} x={x - 4} y={y - 4} width={8} height={8} className="fill-white stroke-french-violet-600" strokeWidth={1.5} />
            );
          })}

        {/* Pins of the picked point: a dimension from the edge it keeps its distance to */}
        {pickedPins}

        {/* Resize handles: LINE handle markers (edit) / draggable footprint edges (test) */}
        {!testMode &&
          handleMarkers.map((f, i) => {
            const [x, y] = project(f);
            return <path key={i} d={`M${x} ${y - 6}L${x + 6} ${y}L${x} ${y + 6}L${x - 6} ${y}Z`} className="fill-neon-green-300 stroke-gray-900" strokeWidth={1} />;
          })}
        {/* A placed object's resize handles: run ends (along the run) and depth handles (across it) */}
        {handles.map((h, i) => {
          const [x, y] = project([h.u, h.n]);
          const along = h.kind !== 'depth';
          return (
            <g key={i} className={along ? 'cursor-ew-resize' : 'cursor-ns-resize'}>
              <circle cx={x} cy={y} r={8} className="fill-neon-green-300 stroke-gray-900" strokeWidth={1.5} />
              <path d={along ? `M${x - 4} ${y}H${x + 4}M${x - 2} ${y - 2}L${x - 4} ${y}L${x - 2} ${y + 2}M${x + 2} ${y - 2}L${x + 4} ${y}L${x + 2} ${y + 2}` : `M${x} ${y - 4}V${y + 4}M${x - 2} ${y - 2}L${x} ${y - 4}L${x + 2} ${y - 2}M${x - 2} ${y + 2}L${x} ${y + 4}L${x + 2} ${y + 2}`} className="stroke-gray-900" strokeWidth={1.2} fill="none" />
            </g>
          );
        })}

        {/* A test door / window's jambs: drag to change the opening width */}
        {jambHandles.map((h) => {
          const [x, y] = project([h.u, h.n]);
          return (
            <g key={h.end} className="cursor-ew-resize">
              <circle cx={x} cy={y} r={8} className="fill-neon-green-300 stroke-gray-900" strokeWidth={1.5} />
              <path d={`M${x - 4} ${y}H${x + 4}M${x - 2} ${y - 2}L${x - 4} ${y}L${x - 2} ${y + 2}M${x + 2} ${y - 2}L${x + 4} ${y}L${x + 2} ${y + 2}`} className="stroke-gray-900" strokeWidth={1.2} fill="none" />
            </g>
          );
        })}

        {/* The test wall's bend handle: drag across the wall to curve it (back onto the line: straight) */}
        {bendHandle &&
          (() => {
            const [x, y] = project(bendHandle);
            const radius = ui.test.wallRadius ?? 0;
            return (
              <g className="cursor-ns-resize">
                <circle cx={x} cy={y} r={8} className="fill-sky-300 stroke-gray-900" strokeWidth={1.5} />
                <path d={`M${x} ${y - 4}V${y + 4}M${x - 2} ${y - 2}L${x} ${y - 4}L${x + 2} ${y - 2}M${x - 2} ${y + 2}L${x} ${y + 4}L${x + 2} ${y + 2}`} className="stroke-gray-900" strokeWidth={1.2} fill="none" />
                <text x={x + 14} y={y + 4} className="pointer-events-none fill-gray-500 text-[11px] dark:fill-gray-400">
                  {radius === 0 ? <T _str="straight" /> : `R ${formatLength(Math.abs(radius))}`}
                </text>
              </g>
            );
          })()}

        {preview && <path d={shapePath(preview)} fill="none" className="stroke-french-violet-600" strokeWidth={1.75} strokeDasharray={drawing?.kind === 'pen' ? undefined : '4 3'} />}
        {drawing?.kind === 'pen' && (
          <g>
            {/* Handles of the node being placed and the one before it (its outgoing side shapes the next segment) */}
            {drawing.nodes.slice(-2).flatMap((n, i) => handleMarks(n, `pen${i}`))}
            {drawing.nodes.map((n, i) => {
              const [x, y] = project(n.point);
              return <rect key={i} x={x - 3.5} y={y - 3.5} width={7} height={7} className={i === 0 && drawing.nodes.length > 2 ? 'fill-white stroke-french-violet-600' : 'fill-french-violet-600'} strokeWidth={1.5} />;
            })}
          </g>
        )}
        {drawing?.kind === 'polyline' &&
          drawing.points.map((p, i) => {
            const [x, y] = project(p);
            return <circle key={i} cx={x} cy={y} r={i === 0 ? 5 : 3} className="fill-french-violet-600" />;
          })}

        {marquee &&
          (() => {
            const [x0, y0] = project([marquee.min[0], marquee.max[1]]);
            const [x1, y1] = project([marquee.max[0], marquee.min[1]]);
            return <rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} className="fill-french-violet-600/10 stroke-french-violet-600" strokeDasharray="3 3" />;
          })()}

        {/* Drawing tools: where the next click lands after snapping (grid, points, Shift angles) */}
        {isDrawingTool &&
          cursor &&
          (() => {
            const [x, y] = project(cursor.point);
            return (
              <g className="pointer-events-none">
                <path d={`M${x - 9} ${y}H${x - 4}M${x + 4} ${y}H${x + 9}M${x} ${y - 9}V${y - 4}M${x} ${y + 4}V${y + 9}`} className="stroke-french-violet-600 dark:stroke-french-violet-400" strokeWidth={1.25} />
                <circle cx={x} cy={y} r={2.75} className="fill-french-violet-600 stroke-white dark:fill-french-violet-400 dark:stroke-eerie-black" strokeWidth={1.25} />
              </g>
            );
          })()}
        {cursor?.target &&
          (() => {
            const [x, y] = project(cursor.target);
            return <circle cx={x} cy={y} r={6} className="fill-none stroke-neon-green-500" strokeWidth={2} />;
          })()}
      </svg>
      <div ref={overlayRef} className="pointer-events-none absolute inset-0" />
      {menu && !testMode && menuItems.length > 0 && <CanvasContextMenu x={menu.x} y={menu.y} bounds={size} items={menuItems} onClose={closeMenu} />}

      {/* Left of the editor's shortcuts button (bottom-right corner), centered on it. */}
      <div className="absolute bottom-4 right-16 flex h-9 items-center gap-2">
        <div className="pointer-events-none rounded bg-white/80 px-2 py-1 font-mono text-[11px] text-gray-600 shadow-sm dark:bg-black/60 dark:text-gray-300">
          {/* X to the right and Y up on screen, whichever way the view is turned. */}
          {cursor ? `X ${(screenSign * cursor.point[0]).toFixed(3)}  Y ${(screenSign * cursor.point[1]).toFixed(3)}` : `${formatLength(minor)} / ${formatLength(major)}`}
        </div>
        <button
          type="button"
          onClick={() => {
            const extent = fitExtent(spec, width, depth);
            setViewport(fitViewport(size, extent.width, extent.depth, turned));
          }}
          className="rounded bg-white/90 px-2 py-1 text-xs shadow-sm hover:bg-white dark:bg-black/60 dark:text-gray-200 dark:hover:bg-black"
        >
          <T _str="fit" swc />
        </button>
      </div>
    </div>
  );
};

export const Canvas2D = memo(Canvas2D_);
